// src/pages/supervisor/SupervisorChat.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Container,
    Box,
    Paper,
    Avatar,
    Typography,
    TextField,
    IconButton,
    Stack,
    CircularProgress,
    Divider,
    Tooltip,
    Badge,
    AppBar,
    Toolbar,
    InputAdornment,
    Fade,
    Zoom,
    Chip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Drawer,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    useTheme,
    useMediaQuery,
    Alert,
    Snackbar,
    Button
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack,
    MoreVert,
    Phone,
    Videocam,
    Info,
    Search,
    AttachFile,
    InsertEmoticon,
    DoneAll,
    Done,
    CheckCircle,
    Person,
    Email,
    School,
    Work,
    Schedule,
    Verified,
    Close,
    Menu as MenuIcon,
    Chat,
    People,
    Refresh,
    Visibility,
    Assignment
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import useCustomToast from '../../hooks/useToast';
import {
    APIAssignStudentBySupervisor,
    APIAssignStudentMessages,
    APIAssignStudentSendMessage,
    APIAssignStudentMarkRead,
    APIAssignStudentUnreadCount,
    APIStudentProfileImage
} from '../../apiServices/APIs';
import { formatCreatedAtDate, getUserDetails, getImageUrl } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import SupervisorBar from '../../components/SupervisorBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper functions
const getInitials = (name) => {
    if (!name) return 'U';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const SupervisorChat = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { chatId } = useParams();
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(!isMobile);
    const [anchorEl, setAnchorEl] = useState(null);
    const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const showToast = useCustomToast();
    const supervisorId = getUserDetails("Id");

    // Scroll to bottom of messages
    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Fetch all chats
    const fetchChats = useCallback(async () => {
        try {
            const result = await apiService.listById(APIAssignStudentBySupervisor, supervisorId);
            if (result.success) {
                const chats = Array.isArray(result.data) ? result.data : [];
                // Add unread count to each chat
                for (let chat of chats) {
                    const unreadResult = await apiService.listById(APIAssignStudentUnreadCount, `${supervisorId}/supervisor`);
                    if (unreadResult.success) {
                        chat.unread_count = unreadResult.data?.unread_count || 0;
                    }
                }
                setAssignments(chats);
                
                // If there's a chatId in URL, select that chat
                if (chatId) {
                    const chat = chats.find(a => a.id === parseInt(chatId));
                    if (chat) {
                        setSelectedChat(chat);
                        await fetchMessages(chat.id);
                    }
                } else if (chats.length > 0 && !selectedChat) {
                    setSelectedChat(chats[0]);
                    await fetchMessages(chats[0].id);
                }
            }
        } catch (error) {
            console.error("Error fetching chats:", error);
        } finally {
            setLoading(false);
        }
    }, [supervisorId, chatId]);

    // Fetch messages for selected chat
    const fetchMessages = async (assignmentId) => {
        try {
            const result = await apiService.listById(APIAssignStudentMessages, assignmentId, {
                user_id: supervisorId,
                user_type: 'supervisor'
            });
            if (result.success) {
                setMessages(Array.isArray(result.data) ? result.data : []);
                // Mark as read
                const formData = new FormData();
                formData.append('assignment_id', assignmentId);
                formData.append('user_id', supervisorId);
                formData.append('user_type', 'supervisor');
                await apiService.update(APIAssignStudentMarkRead, formData);
                // Update unread count in assignments list
                setAssignments(prev => prev.map(chat => 
                    chat.id === assignmentId ? { ...chat, unread_count: 0 } : chat
                ));
                scrollToBottom();
            }
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    // Send message
    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedChat) return;
        
        setSendingMessage(true);
        try {
            const formData = new FormData();
            formData.append('assignment_id', selectedChat.id);
            formData.append('sender_id', supervisorId);
            formData.append('sender_type', 'supervisor');
            formData.append('receiver_id', selectedChat.student.id);
            formData.append('receiver_type', 'student');
            formData.append('message', newMessage);
            
            const result = await apiService.create(APIAssignStudentSendMessage, formData);
            
            if (result && result.success) {
                setMessages(prev => [...prev, result.data]);
                setNewMessage('');
                scrollToBottom();
            } else {
                showToast("Error", result?.message || "Failed to send message", "error");
            }
        } catch (error) {
            console.error("Send message error:", error);
            showToast("Error", error.response?.data?.message || "Failed to send message", "error");
        } finally {
            setSendingMessage(false);
        }
    };

    // Handle chat selection
    const handleSelectChat = async (chat) => {
        setSelectedChat(chat);
        await fetchMessages(chat.id);
        if (isMobile) {
            setDrawerOpen(false);
        }
        // Update URL without reload
        window.history.pushState({}, '', `/supervisor/chat/${chat.id}`);
    };

    // Refresh messages
    const handleRefresh = async () => {
        setRefreshing(true);
        if (selectedChat) {
            await fetchMessages(selectedChat.id);
        }
        await fetchChats();
        setRefreshing(false);
        showToast("Success", "Messages refreshed", "success");
    };

    // Get profile image URL
    const getProfileImageUrl = (student) => {
        if (student?.profile_image) {
            return getImageUrl(APIStudentProfileImage, student.profile_image);
        }
        return null;
    };

    // Get status color
    const getStatusColor = (status) => {
        switch(status) {
            case 'active': return '#4caf50';
            case 'completed': return '#2196f3';
            case 'pending': return '#ff9800';
            default: return '#9e9e9e';
        }
    };

    // Filter chats by search term
    const filteredChats = assignments.filter(chat =>
        chat.student?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chat.student?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        fetchChats();
        // Auto-refresh messages every 10 seconds
        const interval = setInterval(() => {
            if (selectedChat) {
                fetchMessages(selectedChat.id);
            }
        }, 10000);
        
        return () => clearInterval(interval);
    }, [fetchChats, selectedChat]);

    if (loading) {
        return <LoadingState message="Loading Chats..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchChats} title="Unable to Load Chats" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <SupervisorBar notificationCount={5} />
                    
                    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', mt: 1 }}>
                        {/* Chat List Drawer */}
                        <Drawer
                            variant={isMobile ? "temporary" : "persistent"}
                            open={drawerOpen}
                            onClose={() => setDrawerOpen(false)}
                            sx={{
                                width: 320,
                                flexShrink: 0,
                                '& .MuiDrawer-paper': {
                                    width: 320,
                                    boxSizing: 'border-box',
                                    position: isMobile ? 'absolute' : 'relative',
                                    height: 'calc(100vh - 70px)',
                                    top: 'auto',
                                    borderRight: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.background.paper
                                }
                            }}
                        >
                            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.primary.main, color: 'white' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Chat />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                                        Messages
                                    </Typography>
                                    <Chip 
                                        label={assignments.length} 
                                        size="small" 
                                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                                    />
                                    {isMobile && (
                                        <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
                                            <Close />
                                        </IconButton>
                                    )}
                                </Box>
                                <TextField
                                    fullWidth
                                    placeholder="Search chats..."
                                    size="small"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{ 
                                        bgcolor: 'rgba(255,255,255,0.15)', 
                                        borderRadius: 2,
                                        '& .MuiInputBase-input': { color: 'white' },
                                        '& .MuiInputAdornment-root': { color: 'white' }
                                    }}
                                    InputProps={{
                                        startAdornment: <Search sx={{ color: 'white', mr: 1 }} />,
                                    }}
                                />
                            </Box>
                            
                            <Box sx={{ overflowY: 'auto', flex: 1 }}>
                                {filteredChats.length === 0 ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <People sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {searchTerm ? 'No matching chats found' : 'No chats available'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <List sx={{ p: 0 }}>
                                        {filteredChats.map((assignment) => {
                                            const avatarColor = stringToColor(assignment.student?.name);
                                            const initials = getInitials(assignment.student?.name);
                                            const profileImage = getProfileImageUrl(assignment.student);
                                            const isActive = selectedChat?.id === assignment.id;
                                            
                                            return (
                                                <ListItemButton
                                                    key={assignment.id}
                                                    onClick={() => handleSelectChat(assignment)}
                                                    sx={{
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: isActive ? theme.palette.action.selected : 'transparent',
                                                        '&:hover': { bgcolor: theme.palette.action.hover },
                                                        py: 1.5
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Badge
                                                            overlap="circular"
                                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                            variant="dot"
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    bgcolor: getStatusColor(assignment.status),
                                                                    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                                                                }
                                                            }}
                                                        >
                                                            <Avatar 
                                                                src={profileImage}
                                                                sx={{ bgcolor: !profileImage ? avatarColor : 'transparent', width: 48, height: 48 }}
                                                            >
                                                                {!profileImage && initials}
                                                            </Avatar>
                                                        </Badge>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="subtitle2" sx={{ fontWeight: isActive ? 'bold' : 'normal' }}>
                                                                {assignment.student?.name}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                                {assignment.last_message || 'Tap to start chatting'}
                                                            </Typography>
                                                        }
                                                        primaryTypographyProps={{ noWrap: true }}
                                                        secondaryTypographyProps={{ noWrap: true }}
                                                    />
                                                    {assignment.unread_count > 0 && (
                                                        <Badge 
                                                            badgeContent={assignment.unread_count} 
                                                            color="error" 
                                                            sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
                                                        />
                                                    )}
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                )}
                            </Box>
                        </Drawer>

                        {/* Chat Area */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {selectedChat ? (
                                <>
                                    {/* Chat Header */}
                                    <Paper elevation={2} sx={{ borderRadius: 0, p: 1.5, bgcolor: 'white' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {isMobile && (
                                                    <IconButton onClick={() => setDrawerOpen(true)}>
                                                        <MenuIcon />
                                                    </IconButton>
                                                )}
                                                <Avatar 
                                                    src={getProfileImageUrl(selectedChat.student)}
                                                    sx={{ width: 48, height: 48 }}
                                                >
                                                    {!getProfileImageUrl(selectedChat.student) && getInitials(selectedChat.student?.name)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                        {selectedChat.student?.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {selectedChat.student?.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Tooltip title="Refresh">
                                                    <IconButton onClick={handleRefresh} disabled={refreshing}>
                                                        {refreshing ? <CircularProgress size={20} /> : <Refresh />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="View Details">
                                                    <IconButton onClick={() => setInfoDrawerOpen(true)}>
                                                        <Info />
                                                    </IconButton>
                                                </Tooltip>
                                                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                                                    <MoreVert />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Paper>

                                    {/* Messages Area */}
                                    <Box 
                                        ref={chatContainerRef}
                                        sx={{ 
                                            flex: 1, 
                                            overflowY: 'auto', 
                                            p: 2, 
                                            bgcolor: '#f5f5f5',
                                            backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.02) 100%)'
                                        }}
                                    >
                                        <AnimatePresence>
                                            <Stack spacing={1.5}>
                                                {messages.length === 0 ? (
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        flexDirection: 'column', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center',
                                                        height: '100%',
                                                        color: 'text.secondary',
                                                        mt: 10
                                                    }}>
                                                        <Chat sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                                                        <Typography variant="h6">No messages yet</Typography>
                                                        <Typography variant="body2">Start a conversation with {selectedChat.student?.name}</Typography>
                                                    </Box>
                                                ) : (
                                                    messages.map((message, index) => {
                                                        const isOwnMessage = message.sender_type === 'supervisor';
                                                        const showDate = index === 0 || 
                                                            new Date(message.created_at).toDateString() !== new Date(messages[index - 1]?.created_at).toDateString();
                                                        
                                                        return (
                                                            <React.Fragment key={message.id}>
                                                                {showDate && (
                                                                    <Box sx={{ textAlign: 'center', my: 2 }}>
                                                                        <Chip 
                                                                            label={new Date(message.created_at).toLocaleDateString(undefined, { 
                                                                                weekday: 'long', 
                                                                                year: 'numeric', 
                                                                                month: 'long', 
                                                                                day: 'numeric' 
                                                                            })}
                                                                            size="small"
                                                                            sx={{ bgcolor: 'rgba(0,0,0,0.1)' }}
                                                                        />
                                                                    </Box>
                                                                )}
                                                                <motion.div
                                                                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                                                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                                                    transition={{ duration: 0.2 }}
                                                                >
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                                                                        mb: 0.5
                                                                    }}>
                                                                        <Paper elevation={1} sx={{
                                                                            maxWidth: '70%',
                                                                            p: 1.5,
                                                                            borderRadius: 3,
                                                                            bgcolor: isOwnMessage ? theme.palette.primary.main : 'white',
                                                                            color: isOwnMessage ? 'white' : 'text.primary',
                                                                            position: 'relative',
                                                                            borderBottomRightRadius: isOwnMessage ? 1 : 3,
                                                                            borderBottomLeftRadius: isOwnMessage ? 3 : 1
                                                                        }}>
                                                                            <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                                                                                {message.message}
                                                                            </Typography>
                                                                            <Box sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                                mt: 0.5,
                                                                                justifyContent: 'flex-end'
                                                                            }}>
                                                                                <Typography variant="caption" sx={{
                                                                                    fontSize: '0.65rem',
                                                                                    color: isOwnMessage ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                                                                                }}>
                                                                                    {message.formatted_time}
                                                                                </Typography>
                                                                                {isOwnMessage && (
                                                                                    message.is_read ? (
                                                                                        <DoneAll sx={{ fontSize: 12, color: '#4fc3f7' }} />
                                                                                    ) : (
                                                                                        <Done sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }} />
                                                                                    )
                                                                                )}
                                                                            </Box>
                                                                        </Paper>
                                                                    </Box>
                                                                </motion.div>
                                                            </React.Fragment>
                                                        );
                                                    })
                                                )}
                                                <div ref={messagesEndRef} />
                                            </Stack>
                                        </AnimatePresence>
                                    </Box>

                                    {/* Message Input */}
                                    <Paper elevation={2} sx={{ p: 1.5, bgcolor: 'white' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                            <IconButton size="small">
                                                <AttachFile />
                                            </IconButton>
                                            <IconButton size="small">
                                                <InsertEmoticon />
                                            </IconButton>
                                            <TextField
                                                fullWidth
                                                placeholder="Type a message..."
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSendMessage();
                                                    }
                                                }}
                                                multiline
                                                maxRows={4}
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 3,
                                                        backgroundColor: theme.palette.grey[50]
                                                    }
                                                }}
                                            />
                                            <IconButton 
                                                color="primary" 
                                                onClick={handleSendMessage}
                                                disabled={!newMessage.trim() || sendingMessage}
                                                sx={{ 
                                                    bgcolor: theme.palette.primary.main,
                                                    color: 'white',
                                                    '&:hover': { bgcolor: theme.palette.primary.dark },
                                                    '&.Mui-disabled': { bgcolor: theme.palette.grey[300] },
                                                    width: 40,
                                                    height: 40
                                                }}
                                            >
                                                {sendingMessage ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                </>
                            ) : (
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    height: '100%',
                                    color: 'text.secondary'
                                }}>
                                    <Chat sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                                    <Typography variant="h6">Select a chat to start messaging</Typography>
                                    <Typography variant="body2">Choose a student from the list to begin conversation</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Student Info Drawer */}
                <Drawer
                    anchor="right"
                    open={infoDrawerOpen}
                    onClose={() => setInfoDrawerOpen(false)}
                    sx={{ '& .MuiDrawer-paper': { width: 320, p: 2 } }}
                >
                    {selectedChat && (
                        <>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Avatar 
                                    src={getProfileImageUrl(selectedChat.student)}
                                    sx={{ width: 100, height: 100, mx: 'auto', mb: 2 }}
                                >
                                    {!getProfileImageUrl(selectedChat.student) && getInitials(selectedChat.student?.name)}
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {selectedChat.student?.name}
                                </Typography>
                                <Chip 
                                    label={selectedChat.status} 
                                    size="small" 
                                    color={selectedChat.status === 'active' ? 'success' : 'default'}
                                    sx={{ mt: 1 }}
                                />
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Email color="action" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Email</Typography>
                                        <Typography variant="body2">{selectedChat.student?.email}</Typography>
                                    </Box>
                                </Box>
                                {selectedChat.student?.contact_phone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Phone color="action" />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Phone</Typography>
                                            <Typography variant="body2">{selectedChat.student?.contact_phone}</Typography>
                                        </Box>
                                    </Box>
                                )}
                                {selectedChat.student?.nationality && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Person color="action" />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Nationality</Typography>
                                            <Typography variant="body2">{selectedChat.student?.nationality}</Typography>
                                        </Box>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <School color="action" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">University</Typography>
                                        <Typography variant="body2">{selectedChat.student?.university || 'Not assigned'}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Assignment color="action" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Assignment ID</Typography>
                                        <Typography variant="body2">{selectedChat.assignment_id}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Schedule color="action" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Assigned Date</Typography>
                                        <Typography variant="body2">{formatCreatedAtDate(selectedChat.assigned_date)}</Typography>
                                    </Box>
                                </Box>
                                <Tooltip title="View Student Profile">
                                    <Button 
                                        variant="outlined" 
                                        startIcon={<Visibility />}
                                        onClick={() => {
                                            setInfoDrawerOpen(false);
                                            navigate(`/supervisor/student/${selectedChat.student?.id}`);
                                        }}
                                    >
                                        View Full Profile
                                    </Button>
                                </Tooltip>
                            </Stack>
                        </>
                    )}
                </Drawer>

                {/* Menu Dropdown */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                    <MenuItem onClick={() => { setAnchorEl(null); setInfoDrawerOpen(true); }}>
                        <ListItemIcon><Info fontSize="small" /></ListItemIcon>
                        <ListItemText>Contact Info</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { setAnchorEl(null); navigate('/supervisor/assigned-students'); }}>
                        <ListItemIcon><People fontSize="small" /></ListItemIcon>
                        <ListItemText>Back to Students</ListItemText>
                    </MenuItem>
                </Menu>
            </FullWidthContainer>
        </>
    );
};

export default SupervisorChat;